import * as React from 'react';
import { ILocatorOverrides } from '../../../types/utility';
import { createLocators } from '@u1/locators';
import clsx from 'clsx';

import { Typography } from '../Typography';

import useStyles from './Button.styles';

export interface IButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    ILocatorOverrides<HTMLButtonElement> {
  /**
   * Button variant
   * @default primary
   */
  variant?: 'primary' | 'secondary' | 'link' | 'ghost' | 'danger';
  /**
   * Changes button height
   * @default primary
   */
  size?: 'small' | 'medium';
  /**
   * Disables button interactions and applies disabled styles
   */
  disabled?: boolean;
  /**
   * Disables button interactions and displays loading indicator
   */
  loading?: boolean;
  /**
   * Allows the button to cater all the available width
   */
  fullWidth?: boolean;
  /**
   * Icon component to be displayed to the left side of the button text. Accepts React components
   */
  icon?: React.ReactNode;
  /**
   * On click handler
   */
  onClick?(): void;
}

export const buttonLocators = createLocators({
  name: 'Button',
  componentLocators: ['button', 'icon', 'internal', 'text'],
});
const { locators } = buttonLocators();

const Button: React.FC<React.PropsWithChildren<IButtonProps>> = ({
  icon,
  disabled = false,
  loading = false,
  children,
  fullWidth = false,
  type = 'button',
  variant = 'primary',
  size = 'medium',
  onClick,
  locatorsOverrides,
}) => {
  const classes = useStyles();
  const baseAttrs = {
    type,
    disabled: disabled || loading,
    onClick: disabled || loading ? undefined : onClick,
  };

  return (
    <button
      {...locators.button({ variant, size, disabled })}
      {...locatorsOverrides}
      className={clsx(
        classes.root,
        fullWidth && classes.fullWidth,
        icon && !children && classes.iconOnly,
        classes[variant],
        classes[size],
        disabled && classes.disabled,
        loading && classes.loading,
      )}
      {...baseAttrs}
    >
      <div {...locators.internal()} className={classes.internal}>
        {icon && (
          <div {...locators.icon()} className={classes.icon}>
            {icon}
          </div>
        )}
        {children && (
          <Typography
            locatorsOverrides={locators.text()}
            className={classes.content}
            as="div"
            hideOverflow={fullWidth}
            noWrap
          >
            {children}
          </Typography>
        )}
      </div>
    </button>
  );
};

export default Button;
